import { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { RoomEnvironment } from "three/examples/jsm/environments/RoomEnvironment.js";
import { buildLoomAuthoringSelectionScene } from "./loomAuthoringPreview";
import type { LoomNativeHybridAsset } from "./loomNativeHybridAsset";
import { loomProvisionalUnionDataAttributes } from "./loomProvisionalUnionViewer";
import { applyLoomGpuImplicitUnion, LOOM_GPU_UNION_BACKEND } from "./loomGpuImplicitUnion";
import { disposeLoomViewerObject, loomGpuUnionDataAttributes, observeLoomGpuUnionLongTasks,
  recordLoomGpuUnionFrame, releaseLoomGpuProgramRetainers, resolveLoomGpuUnionView } from "./loomGpuImplicitUnionViewer";
import { createLoomViewerFrameScheduler } from "./loomViewerFrameScheduler";
import { LoomActivePartHud } from "./LoomActivePartHud";
import { LoomNativeViewerEvidence } from "./LoomNativeViewerEvidence";
import { addLoomCadStudioRig, addParameterization, addSharedSeams, addSymmetricJacobianLayer,
  addSymmetricTrailingEdgeLayer, authoringGeometrySignature, deriveVisiblePickTargets,
  fitLoomCameraToBounds, loomCameraFitRequired, loomCameraMotionSignature, loomCameraViewSignature,
  loomPickTargetEvidenceEnabled, placeLoomCadGroundGrid, resolveLoomViewportPickAtCanvasPoint,
  syncLoomCadGroundGridVisibility,
  type LoomPickTarget,
} from "./loomNativeViewerHelpers";
import { loomSurfaceMaterialPolicy } from "./loomViewportSurfaceMaterial";
import { applyLoomAuthoredOperandPresentation, buildLoomOwnedSurfacePresentation, loomUntransformedPositionSignature, type LoomSurfacePresentationResult } from "./loomViewportPresentation";
import { useLoomControlSurfaceContext } from "./LoomControlSurfaceContext";
import { LoomControlSurfaceViewerEvidence, loomControlSurfaceViewerDataAttributes } from "./LoomControlSurfaceViewerEvidence";
import { useLoomControlSurfaceViewerScene } from "./useLoomControlSurfaceViewerScene";
import { selectLoomRetainedSurface } from "./loomRetainedSurfaceSelection";
import { addLoomLivePreviewOverlay } from "./loomLivePreviewOverlay";
import { buildLoomViewerCandidate, commitLoomViewerCandidate } from "./loomViewerAtomicScene";
import { LoomBooleanConstructionTraceEvidence } from "./LoomBooleanConstructionTraceEvidence";
import type { LoomNativeHybridViewerProps, LoomNativeHybridViewerRuntime } from "./loomNativeHybridViewerTypes";
import { useLoomBooleanConstructionTraceScene } from "./useLoomBooleanConstructionTraceScene";
import { createLoomViewerCameraSettlementTracker, createLoomViewerInteractionQualityController, recordLoomViewerFrameEvidence } from "./loomViewerInteractionQuality";
import { buildLoomJunctionFairingFootprintOverlay } from "./loomJunctionFairingFootprintOverlay";
export type { LoomNativeHybridLayers } from "./loomNativeHybridViewerTypes";
function token(name: string, fallback: string): string { return getComputedStyle(document.documentElement).getPropertyValue(name).trim() || fallback; }
export function LoomNativeHybridViewer({
  acceptedAssemblySurface = null, asset, analyticPreviewSurface, authoringDocument, authoringPreview, authoringPreviewPhase,
  cameraFitRequest, constructionPhase = null, constructionTrace = null, explicitTrimPreview,
  fairingFootprintPreview = null, gpuUnionPlan = null, jacobianParameterId, jacobianScale,
  layers, onSelectAuthoringNode, provisionalUnionPreview, provisionalUnionRuntime,
  selectedAuthoringNodeId, surfaceAppearance, viewportPresentation,
}: LoomNativeHybridViewerProps) {
  const controlSurfaceContext = useLoomControlSurfaceContext();
  const hostRef = useRef<HTMLDivElement>(null);
  const pickTargetEvidenceEnabledRef = useRef(typeof window !== "undefined" && loomPickTargetEvidenceEnabled(window.location.search));
  const [renderError, setRenderError] = useState<string | null>(null);
  const [renderState, setRenderState] = useState<"loading" | "ready">("loading");
  const [pickTargets, setPickTargets] = useState<readonly LoomPickTarget[]>([]);
  const [previewSignature, setPreviewSignature] = useState("");
  const [jacobianVectorCount, setJacobianVectorCount] = useState(0);
  const [trailingEdgeSegmentCount, setTrailingEdgeSegmentCount] = useState(0);
  const [visibleAuthoringOperandCount, setVisibleAuthoringOperandCount] = useState(0);
  const [activeOutlineCount, setActiveOutlineCount] = useState(0);
  const [gpuUnionApplication, setGpuUnionApplication] = useState({ appliedMeshCount: 0, fallbackReason: null as string | null });
  const [surfacePresentation, setSurfacePresentation] = useState<LoomSurfacePresentationResult | null>(null);
  const explicitTrimAvailable = explicitTrimPreview?.kind === "available";
  const explicitTrimSurface = explicitTrimPreview?.kind === "available" ? explicitTrimPreview : null;
  const provisionalUnionSurface = provisionalUnionPreview?.kind === "available" ? provisionalUnionPreview : null;
  const analyticPreviewActive = explicitTrimAvailable
    && authoringPreviewPhase === "transient" && Boolean(analyticPreviewSurface);
  const gpuUnionView = resolveLoomGpuUnionView(
    gpuUnionPlan, analyticPreviewActive, viewportPresentation, gpuUnionApplication,
  );
  const gpuUnionCandidateActive = gpuUnionView.candidateActive;
  const retained = selectLoomRetainedSurface(acceptedAssemblySurface, authoringPreviewPhase,
    explicitTrimSurface, provisionalUnionSurface);
  const acceptedAssemblyActive = retained.accepted;
  const livePreviewOverlayActive = Boolean(acceptedAssemblySurface && authoringPreviewPhase);
  const displaySurface = gpuUnionCandidateActive ? null : retained.surface;
  const sourceFixtureOverlayAvailable = !gpuUnionCandidateActive && explicitTrimSurface?.method === "reflected-pairwise-hybrid-base";
  const explicitFallbackVisible = Boolean(asset && !displaySurface);
  const parameterLineSegmentCount = explicitTrimPreview?.kind === "available"
    ? Object.values(explicitTrimPreview.parameterLines).reduce((sum, segments) => sum + segments.length / 2, 0)
    : 0;
  const pickSceneRef = useRef<THREE.Group | null>(null);
  const runtimeRef = useRef<LoomNativeHybridViewerRuntime | null>(null);
  const fittedViewRequestRef = useRef<number | null>(null), selectAuthoringNodeRef = useRef(onSelectAuthoringNode);
  useEffect(() => { selectAuthoringNodeRef.current = onSelectAuthoringNode; }, [onSelectAuthoringNode]);
  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    let renderer: THREE.WebGLRenderer;
    try {
      renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, powerPreference: "high-performance" });
    } catch (error) {
      setRenderError(error instanceof Error ? error.message : "WebGL renderer unavailable");
      return;
    }
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1;
    renderer.setClearColor(0x000000, 0);
    renderer.domElement.setAttribute("aria-label", "Interactive Loom smooth CAD surface; click a primitive to select it and drag to orbit");
    renderer.domElement.setAttribute("data-testid", "loom-native-hybrid-canvas");
    renderer.domElement.tabIndex = 0;
    renderer.domElement.style.cursor = "grab";
    host.appendChild(renderer.domElement);
    const scene = new THREE.Scene();
    scene.background = null;
    const pmremGenerator = new THREE.PMREMGenerator(renderer);
    const roomEnvironment = new RoomEnvironment();
    const environmentTarget = pmremGenerator.fromScene(roomEnvironment, 0.04);
    roomEnvironment.dispose();
    scene.environment = environmentTarget.texture;
    const camera = new THREE.PerspectiveCamera(38, 1, 0.01, 1000);
    camera.up.set(0, 0, 1);
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    const group = new THREE.Group();
    const controlSurfaceGroup = new THREE.Group();
    const constructionGroup = new THREE.Group();
    const fairingFootprintGroup = new THREE.Group();
    const studyGroup = new THREE.Group();
    const gpuProgramRetainers = new Map<string, THREE.Material>();
    scene.add(group, controlSurfaceGroup, constructionGroup, fairingFootprintGroup, studyGroup);
    const { grid } = addLoomCadStudioRig(
      scene,
      token("--fc-border-strong", "#8b96a3"),
      token("--fc-border", "#c8d0d8"),
    );
    let renderCount = 0, pickProjectionCount = 0;
    host.dataset.pickProjectionCount = "0";
    let pickProjectionSignature = "", lastCameraMotionSignature = "";
    let updatingControls = false;
    const invalidatePickTargets = () => { pickProjectionSignature = ""; };
    const stopLongTaskObserver = observeLoomGpuUnionLongTasks(host);
    const frameLoop = createLoomViewerFrameScheduler({
      cancelFrame: cancelAnimationFrame,
      requestFrame: requestAnimationFrame,
      onFrame: (advanceControls) => {
        const frameStartedAt = performance.now();
        let controlsChanged = false;
        if (advanceControls) {
          updatingControls = true;
          try { controlsChanged = controls.update(); } finally { updatingControls = false; }
        }
        const cameraViewSignature = loomCameraViewSignature(camera, controls.target);
        const cameraMotionSignature = loomCameraMotionSignature(camera, controls.target);
        const groundGridVisibility = syncLoomCadGroundGridVisibility(grid, camera);
        const cameraChanged = lastCameraMotionSignature !== cameraMotionSignature;
        lastCameraMotionSignature = cameraMotionSignature;
        const keepDamping = advanceControls && controlsChanged && cameraChanged;
        if (keepDamping) interactionQuality.keepMoving();
        else if (interactionQuality.isReduced()) { interactionQuality.refreshOverlays(); interactionQuality.settle(); }
        if (pickTargetEvidenceEnabledRef.current) {
          const bounds = renderer.domElement.getBoundingClientRect();
          const nextPickSignature = `${cameraViewSignature}:${bounds.width.toFixed(1)}:${bounds.height.toFixed(1)}`;
          const pickScene = pickSceneRef.current;
          if (!keepDamping && pickScene && pickProjectionSignature !== nextPickSignature
              && host.dataset.transientUnionBackend !== LOOM_GPU_UNION_BACKEND) {
            camera.updateMatrixWorld(true);
            setPickTargets(deriveVisiblePickTargets(pickScene, camera, bounds.width, bounds.height));
            pickProjectionSignature = nextPickSignature;
            host.dataset.pickProjectionCount = String(++pickProjectionCount);
          }
        }
        if (host.dataset.cameraViewSignature !== cameraViewSignature || host.dataset.groundGridVisibility !== groundGridVisibility) Object.assign(host.dataset, { cameraViewSignature, groundGridVisibility });
        renderer.render(scene, camera);
        recordLoomGpuUnionFrame(host, frameStartedAt);
        recordLoomViewerFrameEvidence(host, renderer, frameStartedAt);
        cameraSettlement.frame(keepDamping);
        host.dataset.renderCount = String(++renderCount);
        const keepConstructionTrace = runtimeRef.current?.advanceConstructionTrace?.(frameStartedAt) ?? false;
        return keepDamping || keepConstructionTrace;
      },
    });
    const interactionQuality = createLoomViewerInteractionQualityController({ host, renderer, root: scene,
      fullPixelRatio: () => Math.min(window.devicePixelRatio || 1, 2), requestRender: () => frameLoop.request() });
    const cameraSettlement = createLoomViewerCameraSettlementTracker(() => runtimeRef.current?.publishControlSurfaceAnchors?.());
    const onControlsChange = () => { if (!updatingControls) { cameraSettlement.changed(); frameLoop.request(true); } };
    const onControlsStart = () => interactionQuality.begin(), onControlsEnd = () => interactionQuality.end();
    controls.addEventListener("change", onControlsChange); controls.addEventListener("start", onControlsStart);
    controls.addEventListener("end", onControlsEnd);
    const observer = new ResizeObserver(([entry]) => {
      const width = Math.max(1, entry?.contentRect.width ?? host.clientWidth);
      const height = Math.max(1, entry?.contentRect.height ?? host.clientHeight);
      renderer.setSize(width, height, false);
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      host.dataset.resizeCount = String(Number(host.dataset.resizeCount ?? 0) + 1);
      invalidatePickTargets();
      frameLoop.request();
    });
    observer.observe(host);
    let pointerStart: { x: number; y: number } | null = null;
    let lastPointerPickAt = 0;
    const selectAt = (clientX: number, clientY: number) => {
      const bounds = renderer.domElement.getBoundingClientRect();
      if (bounds.width <= 0 || bounds.height <= 0) return;
      const nodeId = resolveLoomViewportPickAtCanvasPoint(
        group, camera, clientX - bounds.left, clientY - bounds.top, bounds.width, bounds.height,
      );
      if (typeof nodeId === "string") selectAuthoringNodeRef.current(nodeId);
    };
    const onPointerDown = (event: PointerEvent) => {
      pointerStart = { x: event.clientX, y: event.clientY };
      renderer.domElement.style.cursor = "grabbing";
    };
    const onPointerUp = (event: PointerEvent) => {
      renderer.domElement.style.cursor = "grab";
      const start = pointerStart;
      pointerStart = null;
      if (!start || Math.hypot(event.clientX - start.x, event.clientY - start.y) > 5) return;
      selectAt(event.clientX, event.clientY);
      lastPointerPickAt = performance.now();
    };
    const onClick = (event: MouseEvent) => {
      if (performance.now() - lastPointerPickAt < 80) return;
      selectAt(event.clientX, event.clientY);
    };
    renderer.domElement.addEventListener("pointerdown", onPointerDown);
    renderer.domElement.addEventListener("pointerup", onPointerUp);
    renderer.domElement.addEventListener("click", onClick);
    runtimeRef.current = { camera, constructionGroup, controlSurfaceGroup, controls, fairingFootprintGroup,
      gpuProgramRetainers, grid, group,
      invalidatePickTargets, renderer, requestRender: frameLoop.request, studyGroup };
    frameLoop.request();
    return () => {
      frameLoop.dispose();
      interactionQuality.dispose();
      stopLongTaskObserver();
      observer.disconnect();
      renderer.domElement.removeEventListener("pointerdown", onPointerDown);
      renderer.domElement.removeEventListener("pointerup", onPointerUp);
      renderer.domElement.removeEventListener("click", onClick);
      controls.removeEventListener("change", onControlsChange);
      controls.removeEventListener("start", onControlsStart);
      controls.removeEventListener("end", onControlsEnd);
      controls.dispose();
      disposeLoomViewerObject(scene);
      releaseLoomGpuProgramRetainers(gpuProgramRetainers);
      environmentTarget.dispose();
      pmremGenerator.dispose();
      renderer.dispose();
      renderer.forceContextLoss();
      if (renderer.domElement.parentElement === host) {
        host.removeChild(renderer.domElement);
      }
      fittedViewRequestRef.current = null;
      pickSceneRef.current = null;
      runtimeRef.current = null;
    };
  }, []);
  useEffect(() => {
    const runtime = runtimeRef.current;
    if (!runtime) return;
    try { const sceneStartedAt = performance.now();
      setRenderError(null);
      setRenderState("loading");
      const keepGpuProgram = gpuUnionCandidateActive && gpuUnionPlan?.kind === "available"
        ? gpuUnionPlan.planSignature : undefined;
      const host = hostRef.current;
      const activeColor = token("--fc-warning", "#d28a36");
      const overlaysPresented = viewportPresentation.assemblyView === "assembled";
      const retainedSurfaceVisible = layers.surface || layers.tessellation;
      let sourceSignature = "";
      let presentation: LoomSurfacePresentationResult;
      let nextGpuApplication = { appliedMeshCount: 0, fallbackReason: null as string | null };
      let nextJacobianVectorCount = 0;
      let nextTrailingEdgeSegmentCount = 0;
      const candidate = buildLoomViewerCandidate((candidateGroup) => {
        if (!gpuUnionCandidateActive && displaySurface) {
          presentation = buildLoomOwnedSurfacePresentation(
            displaySurface,
            authoringDocument, selectedAuthoringNodeId, viewportPresentation, surfaceAppearance,
            token("--fc-cad-surface", "#527c90"), activeColor,
          );
          presentation.group.visible = retainedSurfaceVisible;
          candidateGroup.add(presentation.group);
          sourceSignature = loomUntransformedPositionSignature(displaySurface.positions);
          if (!acceptedAssemblyActive && explicitTrimSurface
              && layers.parameterization && overlaysPresented) {
            const parameterAsset: LoomNativeHybridAsset = {
              ...asset,
              surface: {
                faces: explicitTrimSurface.faces,
                parameterLines: explicitTrimSurface.parameterLines,
                positions: explicitTrimSurface.positions,
              },
            };
            addParameterization(candidateGroup, parameterAsset);
          }
          if (!acceptedAssemblyActive && explicitTrimSurface
              && layers.sharedSeam && overlaysPresented) {
            addSharedSeams(candidateGroup, explicitTrimSurface.positions, explicitTrimSurface.seamRings);
          }
          if (!acceptedAssemblyActive && layers.trailingEdge
              && sourceFixtureOverlayAvailable && overlaysPresented) {
            nextTrailingEdgeSegmentCount = addSymmetricTrailingEdgeLayer(
              candidateGroup, asset, token("--fc-danger", "#b44555"),
            );
          }
          if (!acceptedAssemblyActive && layers.jacobian
              && sourceFixtureOverlayAvailable && overlaysPresented) {
            nextJacobianVectorCount = addSymmetricJacobianLayer(
              candidateGroup, asset, jacobianParameterId, jacobianScale, token("--fc-info", "#2368a2"),
            );
          }
        } else {
          const authoringScene = buildLoomAuthoringSelectionScene(
            authoringDocument, selectedAuthoringNodeId, activeColor, "edited-preview", "#587786",
          );
          sourceSignature = authoringGeometrySignature(authoringScene);
          presentation = applyLoomAuthoredOperandPresentation(
            authoringScene, authoringDocument, selectedAuthoringNodeId, viewportPresentation, activeColor,
          );
          if (gpuUnionCandidateActive && gpuUnionPlan?.kind === "available") {
            const application = applyLoomGpuImplicitUnion(authoringScene, gpuUnionPlan);
            nextGpuApplication = application.unmatchedOperandIds.length
              ? { appliedMeshCount: 0, fallbackReason: `missing explicit operands: ${application.unmatchedOperandIds.join(", ")}` }
              : { appliedMeshCount: application.appliedMeshCount, fallbackReason: null };
          }
          candidateGroup.add(authoringScene);
        }
        if (livePreviewOverlayActive && acceptedAssemblySurface) {
          addLoomLivePreviewOverlay(candidateGroup, presentation!, acceptedAssemblySurface, explicitTrimSurface,
            authoringDocument, selectedAuthoringNodeId, viewportPresentation, acceptedAssemblyActive,
            token("--fc-cad-surface", "#527c90"), activeColor);
        }
        if (displaySurface) {
          const selectionProxies = buildLoomAuthoringSelectionScene(
            authoringDocument, selectedAuthoringNodeId, activeColor, "pick-only", "#587786",
          );
          selectionProxies.name = "Loom invisible authoring selection proxies";
          selectionProxies.visible = viewportPresentation.assemblyView === "assembled";
          candidateGroup.add(selectionProxies);
        }
        return presentation!;
      }, disposeLoomViewerObject);
      presentation = candidate.value;
      commitLoomViewerCandidate(runtime.group, candidate.group, (child) => disposeLoomViewerObject(
        child, gpuUnionCandidateActive ? runtime.gpuProgramRetainers : undefined,
      ));
      runtime.refreshControlSurfaceSource?.();
      releaseLoomGpuProgramRetainers(runtime.gpuProgramRetainers, keepGpuProgram);
      if (host) host.dataset.gpuUnionRetainedProgramCount = String(runtime.gpuProgramRetainers.size);
      setJacobianVectorCount(nextJacobianVectorCount);
      setTrailingEdgeSegmentCount(nextTrailingEdgeSegmentCount);
      setSurfacePresentation(presentation);
      setGpuUnionApplication(nextGpuApplication);
      setActiveOutlineCount(retainedSurfaceVisible || !displaySurface ? presentation.outlineCount : 0);
      pickSceneRef.current = runtime.group;
      const shouldFit = loomCameraFitRequired(fittedViewRequestRef.current, cameraFitRequest);
      if (!presentation.bounds.isEmpty() && shouldFit) {
        const damping = runtime.controls.enableDamping;
        runtime.controls.enableDamping = false;
        fitLoomCameraToBounds(runtime.camera, runtime.controls, presentation.bounds);
        runtime.controls.enableDamping = damping;
        fittedViewRequestRef.current = cameraFitRequest;
      }
      if (!presentation.bounds.isEmpty()) placeLoomCadGroundGrid(runtime.grid, presentation.bounds);
      runtime.group.updateMatrixWorld(true);
      runtime.camera.updateMatrixWorld(true);
      setPreviewSignature(sourceSignature);
      runtime.invalidatePickTargets?.();
      runtime.requestRender?.();
      if (host) host.dataset.sceneRebuildCount = String(Number(host.dataset.sceneRebuildCount ?? 0) + 1);
      if (host && gpuUnionCandidateActive && !nextGpuApplication.fallbackReason) {
        host.dataset.gpuUnionGeometryRevision = String(Number(host.dataset.gpuUnionGeometryRevision ?? 0) + 1); host.dataset.gpuUnionSceneBuildMs = (performance.now() - sceneStartedAt).toFixed(3);
      }
      let visibleOperands = 0;
      presentation.group.traverse((object) => {
        const mesh = object as THREE.Mesh<THREE.BufferGeometry, THREE.Material>;
        if (mesh.isMesh && !displaySurface && !Array.isArray(mesh.material) && mesh.material.opacity > 0
            && mesh.userData.loomVisualRole !== "active-part-fill") visibleOperands += 1;
      });
      setVisibleAuthoringOperandCount(visibleOperands);
      setRenderState("ready");
    } catch (error) {
      setRenderError(error instanceof Error ? error.message : "Unable to render the Loom surface");
      setRenderState(runtime.group.children.length ? "ready" : "loading");
    }
  }, [acceptedAssemblyActive, acceptedAssemblySurface, asset, authoringDocument, cameraFitRequest, displaySurface,
    explicitFallbackVisible, explicitTrimSurface, gpuUnionCandidateActive, gpuUnionPlan,
    jacobianParameterId, jacobianScale, layers, selectedAuthoringNodeId,
    livePreviewOverlayActive, sourceFixtureOverlayAvailable, surfaceAppearance, viewportPresentation]);
  useEffect(() => {
    const runtime = runtimeRef.current;
    if (!runtime) return;
    const candidate = new THREE.Group();
    let guideCount = 0;
    let shapeGuideCount = 0;
    if (fairingFootprintPreview?.kind === "available"
        && viewportPresentation.assemblyView === "assembled") {
      const overlay = buildLoomJunctionFairingFootprintOverlay(fairingFootprintPreview, {
        fuselage: token("--fc-warning", "#c77620"),
        wing: token("--fc-info", "#1683a6"),
      });
      candidate.add(overlay.group);
      guideCount = overlay.receipt.guideCount;
      shapeGuideCount = overlay.receipt.shapeGuideCount;
    }
    commitLoomViewerCandidate(runtime.fairingFootprintGroup, candidate, disposeLoomViewerObject);
    const host = hostRef.current;
    if (host) {
      host.dataset.fairingFootprintGuideCount = String(guideCount);
      host.dataset.fairingShapeGuideCount = String(shapeGuideCount);
      host.dataset.fairingFootprintOverlayUpdateCount = String(
        Number(host.dataset.fairingFootprintOverlayUpdateCount ?? 0) + 1,
      );
    }
    runtime.requestRender?.();
  }, [fairingFootprintPreview, viewportPresentation.assemblyView]);
  useLoomBooleanConstructionTraceScene(
    runtimeRef, constructionTrace,
    acceptedAssemblyActive && viewportPresentation.assemblyView === "assembled",
  );
  const controlSurfaceReceipt = useLoomControlSurfaceViewerScene(runtimeRef, authoringDocument);
  const gpuUnionRenderActive = gpuUnionView.renderActive;
  const previewBackend = gpuUnionRenderActive ? LOOM_GPU_UNION_BACKEND : provisionalUnionSurface
    ? "provisional-explicit-classification"
    : !explicitTrimSurface ? "none"
    : analyticPreviewActive
      ? "analytic-jacobian-linearization"
      : explicitTrimSurface.method === "frozen-topology-jacobian-linearization"
        ? "symmetric-explicit-trim-linearization"
        : "symmetric-explicit-trim-base";
  const presentationMode = layers.surface
    ? layers.tessellation ? surfaceAppearance === "ghost-inspection" ? "ghost-with-wireframe" : "satin-with-wireframe" : surfaceAppearance === "ghost-inspection" ? "ghost-surface" : "satin-surface"
    : layers.tessellation ? "wireframe" : "overlays-only";
  const materialPolicy = loomSurfaceMaterialPolicy(surfaceAppearance);
  const inspectionSurfaceKind = acceptedAssemblyActive ? "accepted-occurrence-assembly"
    : gpuUnionRenderActive ? "gpu-implicit-union" : explicitTrimSurface
      ? "symmetric-explicit-trim" : provisionalUnionSurface
        ? "provisional-explicit-union" : "authored-operands";
  const overlaysPresented = viewportPresentation.assemblyView === "assembled";
  const retainedSurfaceVisible = layers.surface || layers.tessellation;
  const highlightVisible = Boolean(surfacePresentation && surfacePresentation.highlightFaceCount > 0
    && (retainedSurfaceVisible || !displaySurface));
  const actualSurfaceStyle = retainedSurfaceVisible || !displaySurface ? viewportPresentation.surfaceStyle : "hidden";
  return (
    <div
      className="pg-viewer loom-cad-viewport"
      {...loomControlSurfaceViewerDataAttributes(controlSurfaceContext, controlSurfaceReceipt)}
      data-colour-profile={explicitFallbackVisible ? "semantic-aerospace-components" : "aerospace-blue-enamel"}
      data-camera-fit-policy="initial-or-explicit-reset"
      data-camera-fit-request={cameraFitRequest}
      data-environment-profile="procedural-room-pmrem"
      data-lighting-rig="studio-form-key-fill-rim"
      data-material-profile={explicitFallbackVisible ? "lit-authored-operands" : surfaceAppearance === "ghost-inspection" ? provisionalUnionSurface ? "ghost-provisional-explicit-union" : "ghost-symmetric-explicit-trim" : provisionalUnionSurface ? "satin-provisional-union" : "satin-explicit-surface"}
      data-inspection-surface-kind={inspectionSurfaceKind}
      data-active-fill-visible={String(Boolean(surfacePresentation?.activeFillVisible && highlightVisible))}
      data-assembly-view={viewportPresentation.assemblyView}
      data-explode-amount={viewportPresentation.explodeAmount.toFixed(2)}
      data-explode-offset-signature={surfacePresentation?.offsetSignature ?? "none"}
      data-exploded-entity-count={surfacePresentation?.explodedEntityCount ?? 0}
      data-face-owner-count={surfacePresentation?.entityCount ?? 0}
      data-fairing-footprint-authority={fairingFootprintPreview?.kind === "available"
        ? fairingFootprintPreview.authority : "none"}
      data-fairing-footprint-fuselage-signature={fairingFootprintPreview?.kind === "available"
        ? fairingFootprintPreview.parentSignatures.fuselage : "none"}
      data-fairing-footprint-preview={fairingFootprintPreview?.kind === "available"
        ? "projected-parent-contacts-and-shape-leads" : "off"}
      data-fairing-footprint-reason={fairingFootprintPreview?.kind === "unavailable"
        ? fairingFootprintPreview.reason : "none"}
      data-fairing-footprint-signature={fairingFootprintPreview?.kind === "available"
        ? fairingFootprintPreview.signature : "none"}
      data-fairing-footprint-wing-signature={fairingFootprintPreview?.kind === "available"
        ? fairingFootprintPreview.parentSignatures.wing : "none"}
      data-fairing-shape-signature={fairingFootprintPreview?.kind === "available"
        ? fairingFootprintPreview.shapeSignature : "none"}
      data-face-ownership={acceptedAssemblyActive ? "accepted-occurrence-face-owners"
        : displaySurface ? "retained-explicit-face-owners" : "visible-authored-operand-owners"}
      data-highlight-face-count={surfacePresentation?.highlightFaceCount ?? 0}
      data-highlight-geometry-source={displaySurface ? "retained-display-faces" : "authored-visible-operands"}
      data-highlight-node-id={selectedAuthoringNodeId}
      data-highlight-owner-count={surfacePresentation?.highlightOwnerCount ?? 0}
      data-highlight-visible={String(highlightVisible)}
      data-overlays-suppressed-by-view={String(!overlaysPresented)}
      data-presentation-state={`${actualSurfaceStyle}:${viewportPresentation.assemblyView}:${viewportPresentation.explodeAmount.toFixed(2)}`}
      data-presentation-transform-only="true"
      data-presentation-unavailable-reason={surfacePresentation?.unavailableReason ?? "none"}
      data-presented-geometry-signature={surfacePresentation?.presentedGeometrySignature ?? "none"}
      data-presentation-mode={presentationMode}
      data-preview-geometry-signature={previewSignature}
      {...loomGpuUnionDataAttributes(gpuUnionPlan, gpuUnionView, gpuUnionApplication)}
      data-preview-revision={provisionalUnionSurface ? provisionalUnionRuntime.renderedRevision ?? "none" : authoringDocument.revision}
      data-display-mode={acceptedAssemblyActive ? "accepted-occurrence-assembly"
        : gpuUnionRenderActive ? "gpu-implicit-union-preview" : explicitTrimSurface
          ? "symmetric-explicit-trim" : provisionalUnionSurface
            ? "provisional-explicit-union" : "explicit-operands"}
      data-explicit-trim-boundary-edges={explicitTrimSurface?.boundaryEdgeCount ?? "none"}
      data-explicit-trim-build-ms={explicitTrimSurface?.buildMs.toFixed(2) ?? "none"}
      data-explicit-trim-face-count={explicitTrimSurface ? explicitTrimSurface.faces.length / 3 : 0}
      data-explicit-trim-method={explicitTrimSurface?.method ?? "none"}
      data-explicit-trim-seam-rings={explicitTrimSurface?.seamRings.length ?? 0}
      data-explicit-trim-state={explicitTrimSurface ? "available" : "unavailable"}
      {...loomProvisionalUnionDataAttributes(provisionalUnionSurface, provisionalUnionRuntime)}
      data-preview-backend={previewBackend}
      data-pick-target-evidence={pickTargetEvidenceEnabledRef.current ? "enabled" : "disabled"}
      data-preview-jacobian-delta={analyticPreviewActive ? analyticPreviewSurface?.deltaJacobian : "none"}
      data-preview-parameter={analyticPreviewActive ? analyticPreviewSurface?.parameterId : "none"}
      data-accepted-occurrence-surface-count={acceptedAssemblyActive && layers.surface ? 1 : 0}
      data-rendered-preview-surface-count={!acceptedAssemblyActive
        && (gpuUnionRenderActive || displaySurface) && layers.surface ? 1 : 0}
      data-live-preview-mode={livePreviewOverlayActive ? "amber-candidate-over-ghost-accepted" : "off"} data-retained-accepted-overlay-count={livePreviewOverlayActive && layers.surface ? 1 : 0}
      data-rendered-filled-mesh-count={retainedSurfaceVisible || !displaySurface ? surfacePresentation?.filledMeshCount ?? 0 : 0}
      data-rendered-shared-seam-count={!gpuUnionRenderActive && explicitTrimSurface && layers.sharedSeam && overlaysPresented ? explicitTrimSurface.seamRings.length : 0}
      data-rendered-shared-seam-visible={String(Boolean(!gpuUnionRenderActive && explicitTrimSurface && layers.sharedSeam && overlaysPresented))}
      data-rendered-tessellation-visible={String(Boolean(displaySurface && layers.tessellation))}
      data-rendered-wire-mesh-count={retainedSurfaceVisible || !displaySurface ? surfacePresentation?.wireMeshCount ?? 0 : 0}
      data-rendered-jacobian-vector-count={jacobianVectorCount}
      data-rendered-jacobian-visible={String(jacobianVectorCount > 0)}
      data-rendered-trailing-edge-count={trailingEdgeSegmentCount}
      data-rendered-trailing-edge-visible={String(trailingEdgeSegmentCount > 0)}
      data-render-state={renderState}
      data-frame-policy="invalidate-with-damping"
      data-renderer-clear-contract={surfaceAppearance === "ghost-inspection" ? "transparent-canvas-ghost-inspection-surface" : "transparent-canvas-opaque-surface"} data-surface-normal-policy={gpuUnionRenderActive ? "explicit-normals-with-implicit-fragment-discard" : provisionalUnionSurface ? "owner-field-gradient-outward" : "indexed-area-weighted"}
      data-selected-node={selectedAuthoringNodeId}
      data-active-outline-node-id={selectedAuthoringNodeId}
      data-active-outline-visible={String(activeOutlineCount > 0)} data-active-outline-depth-policy={surfacePresentation?.group.userData.loomActiveOutlineDepthPolicy ?? "none"}
      data-source-fixture-overlay-visible={String(Boolean(sourceFixtureOverlayAvailable && overlaysPresented && (layers.trailingEdge || layers.jacobian)))}
      data-shading-profile="physical-smooth-normals"
      data-stale-extraction-visible={authoringPreview}
      data-surface-appearance={surfaceAppearance}
      data-surface-style={actualSurfaceStyle}
      data-surface-inspection-semantics={surfaceAppearance === "ghost-inspection" ? "visual-only-no-geometry-change" : "off"}
      data-surface-material-depth-test={String(materialPolicy.depthTest)} data-surface-material-depth-write={String(materialPolicy.depthWrite)} data-surface-material-opacity={materialPolicy.opacity} data-surface-material-side={materialPolicy.sideLabel} data-surface-material-transmission={materialPolicy.transmission} data-surface-material-transparent={String(materialPolicy.transparent)}
      data-trim-fallback={gpuUnionRenderActive ? "gpu-implicit-visual-only" : explicitFallbackVisible ? "explicit-operands" : provisionalUnionSurface ? "provisional-explicit-union" : "none"}
      data-trim-unavailable-reason={explicitTrimPreview?.kind === "unavailable" ? explicitTrimPreview.reason : "none"}
      data-visible-authoring-operand-count={visibleAuthoringOperandCount}
      data-visible-entity-count={retainedSurfaceVisible || !displaySurface ? surfacePresentation?.visibleEntityCount ?? 0 : 0}
      data-testid="loom-native-hybrid-viewer"
      data-texture-profile="procedural-studio-reflection"
      ref={hostRef}
    >
      <LoomBooleanConstructionTraceEvidence phase={constructionPhase} trace={constructionTrace} />
      <LoomActivePartHud document={authoringDocument} outlineCount={activeOutlineCount} selectedNodeId={selectedAuthoringNodeId} />
      <LoomControlSurfaceViewerEvidence receipt={controlSurfaceReceipt} />
      <LoomNativeViewerEvidence {...{
        analyticPreviewActive, analyticPreviewSurface, authoringPreview, explicitTrimPreview,
        explicitTrimSurface, gpuUnionActive: gpuUnionRenderActive,
        gpuUnionOperandCount: gpuUnionPlan?.kind === "available" ? gpuUnionPlan.operandIds.length : 0,
        jacobianParameterId, jacobianVectorCount, layers, overlaysPresented,
        livePreviewOverlayActive,
        parameterLineSegmentCount, pickTargets, provisionalUnionPreview, provisionalUnionSurface,
        presentationUnavailableReason: surfacePresentation?.unavailableReason ?? null,
        renderError, sourceFixtureOverlayAvailable, trailingEdgeSegmentCount,
      }} />
    </div>
  );
}
